import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

declare module '@mui/material/styles' {
  interface Palette {
    red: Palette['primary'];
    teal: Palette['primary'];
    green: Palette['primary'];
    black: Palette['primary'];
    violet: Palette['primary'];
  }
  interface PaletteOptions {
    red: PaletteOptions['primary'];
    teal: PaletteOptions['primary'];
    green: PaletteOptions['primary'];
    black: PaletteOptions['primary'];
    violet: PaletteOptions['primary'];
  }
}

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('themeMode');
      return (saved as ThemeMode) || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('themeMode', mode);
      document.documentElement.setAttribute('data-theme', mode);
    }
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#4ECDC4',
            light: '#7EDDD6',
            dark: '#3BA99F',
            contrastText: '#FFFFFF'
          },
          secondary: {
            main: '#424242',
            light: '#6D6D6D',
            dark: '#1B1B1B',
            contrastText: '#FFFFFF'
          },
          background: {
            default: mode === 'light' ? '#F5F5F5' : '#121212',
            paper: mode === 'light' ? '#FFFFFF' : '#1E1E1E'
          },
          text: {
            primary: mode === 'light' ? '#1E1E1E' : '#FFFFFF',
            secondary: mode === 'light' ? '#616161' : '#B0B0B0'
          },
          red: {
            main: '#EF5350'
          },
          teal: {
            main: '#4ECDC4'
          },
          violet: {
            main: '#AB47BC'
          },
          black: {
            main: '#000000'
          },
          green: {
            main: '#66BB6A'
          },
          divider: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
          action: {
            active: '#4ECDC4',
            hover: mode === 'light' ? 'rgba(78, 205, 196, 0.15)' : 'rgba(78, 205, 196, 0.08)',
            selected: mode === 'light' ? 'rgba(78, 205, 196, 0.25)' : 'rgba(78, 205, 196, 0.16)',
            disabled: mode === 'light' ? 'rgba(0, 0, 0, 0.26)' : 'rgba(255, 255, 255, 0.3)',
            disabledBackground: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'
          }
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontWeight: 700,
            fontSize: '2.5rem',
            letterSpacing: '-0.01562em'
          },
          h2: {
            fontWeight: 600,
            fontSize: '2rem',
            letterSpacing: '-0.00833em'
          },
          h3: {
            fontWeight: 600,
            fontSize: '1.75rem',
            letterSpacing: '0em'
          },
          h4: {
            fontWeight: 600,
            fontSize: '1.5rem',
            letterSpacing: '0.00735em'
          },
          h5: {
            fontWeight: 600,
            fontSize: '1.25rem',
            letterSpacing: '0em'
          },
          h6: {
            fontWeight: 600,
            fontSize: '1rem',
            letterSpacing: '0.0075em'
          },
          button: {
            fontWeight: 600,
            textTransform: 'none'
          }
        },
        shape: {
          borderRadius: 12
        },
        components: {
          MuiTextField: {
            defaultProps: {
              variant: 'outlined'
            },
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(78, 205, 196, 0.5)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4ECDC4'
                  }
                }
              }
            }
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                padding: '10px 24px',
                fontSize: '0.95rem',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(78, 205, 196, 0.3)'
                }
              },
              contained: {
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(78, 205, 196, 0.4)'
                }
              },
              containedPrimary: {
                color: '#FFFFFF !important',
                '&:hover': {
                  color: '#FFFFFF !important'
                },
                '&:active': {
                  color: '#FFFFFF !important'
                },
                '&:focus': {
                  color: '#FFFFFF !important'
                },
                '& .MuiButton-label': {
                  color: '#FFFFFF !important'
                }
              },
              outlined: {
                borderColor: '#878787',
                color: mode === 'light' ? '#1E1E1E' : '#FFFFFF',
                '&:hover': {
                  borderColor: '#878787',
                  backgroundColor: 'rgba(135, 135, 135, 0.08)'
                }
              },
              textPrimary: {
                color: mode === 'light' ? '#3BA99F !important' : '#4ECDC4 !important',
                '&:hover': {
                  backgroundColor: mode === 'light' ? 'rgba(78, 205, 196, 0.08)' : 'rgba(78, 205, 196, 0.08)',
                  color: mode === 'light' ? '#3BA99F !important' : '#4ECDC4 !important'
                },
                '&:active': {
                  color: mode === 'light' ? '#3BA99F !important' : '#4ECDC4 !important'
                },
                '& .MuiButton-startIcon': {
                  color: mode === 'light' ? '#3BA99F !important' : '#4ECDC4 !important'
                }
              }
            }
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                backgroundColor: mode === 'light' ? '#FFFFFF' : '#1E1E1E',
                boxShadow: mode === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 20px rgba(0, 0, 0, 0.5)'
              },
              elevation1: {
                boxShadow: mode === 'light' ? '0 1px 4px rgba(0, 0, 0, 0.08)' : '0 2px 8px rgba(0, 0, 0, 0.4)'
              },
              elevation2: {
                boxShadow: mode === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 12px rgba(0, 0, 0, 0.5)'
              },
              elevation3: {
                boxShadow: mode === 'light' ? '0 4px 12px rgba(0, 0, 0, 0.12)' : '0 8px 24px rgba(0, 0, 0, 0.6)'
              }
            }
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 16,
                backgroundColor: mode === 'light' ? '#FFFFFF' : '#1E1E1E',
                boxShadow: mode === 'light' ? '0 2px 12px rgba(0, 0, 0, 0.08)' : '0 4px 20px rgba(0, 0, 0, 0.5)'
              }
            }
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === 'light' ? '#FFFFFF' : '#1A1A1A',
                borderRight: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(255, 255, 255, 0.12)'
              }
            }
          },
          MuiListItem: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  backgroundColor: 'rgba(78, 205, 196, 0.16)',
                  '&:hover': {
                    backgroundColor: 'rgba(78, 205, 196, 0.24)'
                  }
                }
              }
            }
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                color: '#4ECDC4',
                '&:hover': {
                  backgroundColor: 'rgba(78, 205, 196, 0.08)'
                }
              }
            }
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                '&.MuiTableRow-hover:hover': {
                  backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
                  cursor: 'pointer'
                }
              }
            }
          }
        }
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
